// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: "Waiting on a reply", both ways, from GET /konvo/assistant/waiting. One list feeds every
// place it shows (design/waiting-replies): the Waiting chip in Chats, the bar in an open mail or
// chat, the tabs on Home's to-do card and the Waiting filter on the Scheduled page. The corner's
// pop-up comes from the server's planner and opens the same item here.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {dueItems} from '@app/features/konvo_waiting/utils/WaitingModel';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import type {
	WaitingAction,
	WaitingDirection,
	WaitingDraftChoice,
	WaitingDraftRequest,
	WaitingDraftResponse,
	WaitingItem,
	WaitingListResponse,
	WaitingVia,
} from '@fluxer/schema/src/domains/konvo/Waiting';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('KonvoWaiting');

/** Repeat refreshes within this window reuse the last answer (Chats, Home and a thread all ask). */
const FRESH_MS = 60_000;
/** While Konvo is open, look again this often: answers arrive by themselves. */
const POLL_MS = 5 * 60_000;

export interface WaitingDraft {
	text: string;
	via: WaitingVia;
	loading: boolean;
	sending: boolean;
	error: boolean;
}

/** The answers Konvo offers for someone waiting on you (the bar's chips), by item id. */
export interface WaitingChoices {
	choices: Array<WaitingDraftChoice>;
	via: WaitingVia;
	loading: boolean;
	error: boolean;
}

/**
 * A Konvo draft sitting in a conversation's own message box (design/waiting-draft A2), by
 * `source:conversationId`. `history` holds the box's earlier texts for Undo; its first entry is what
 * the box held before Konvo's draft went in.
 */
export interface WaitingBoxDraft {
	itemId: string;
	via: WaitingVia;
	history: Array<string>;
	changing: boolean;
	error: boolean;
}

export const boxKey = (source: string, conversationId: string) => `${source}:${conversationId}`;

class WaitingStore {
	enabled = false;
	items: Array<WaitingItem> = [];
	answeredWeek = 0;
	defaultHours = 24;
	loaded = false;
	/** The clock the lists use; ticks with each refresh so items come up without a reload. */
	now = Date.now();
	/** Chats shows the Waiting list instead of the chats (this device, this session). */
	chatsOpen = false;
	/** Drafts in progress, by item id (the bar and the Today rows share them). */
	drafts = new Map<string, WaitingDraft>();
	/** Answer choices by item id (the thread bar). */
	choices = new Map<string, WaitingChoices>();
	/** Konvo drafts in message boxes, by boxKey. */
	boxDrafts = new Map<string, WaitingBoxDraft>();
	/** An item the corner asked to open with its draft started. */
	focusId: string | null = null;
	private fetchedAt = 0;
	private inflight: Promise<void> | null = null;
	private timer: ReturnType<typeof setInterval> | null = null;

	constructor() {
		makeAutoObservable<this, 'fetchedAt' | 'inflight' | 'timer'>(this, {
			fetchedAt: false,
			inflight: false,
			timer: false,
		});
	}

	get onYou(): Array<WaitingItem> {
		return dueItems(this.items, this.now, 'on_you');
	}

	get onThem(): Array<WaitingItem> {
		return dueItems(this.items, this.now, 'on_them');
	}

	/** Up now, both ways: the Waiting chip's number. */
	get dueCount(): number {
		return this.onYou.length + this.onThem.length;
	}

	byId(id: string): WaitingItem | undefined {
		return this.items.find((item) => item.id === id);
	}

	/** Starts the periodic refresh (idempotent). */
	start(): void {
		if (this.timer) return;
		void this.refresh();
		this.timer = setInterval(() => {
			if (typeof document === 'undefined' || document.visibilityState === 'visible') void this.refresh({force: true});
		}, POLL_MS);
		if (typeof window !== 'undefined') {
			window.addEventListener('focus', () => void this.refresh());
		}
	}

	async refresh(options: {force?: boolean} = {}): Promise<void> {
		if (!options.force && this.loaded && Date.now() - this.fetchedAt < FRESH_MS) {
			this.now = Date.now();
			return;
		}
		if (this.inflight) return this.inflight;
		this.inflight = (async () => {
			try {
				const response = await http.get<WaitingListResponse>(Endpoints.KONVO_ASSISTANT_WAITING);
				runInAction(() => {
					const body = response.body;
					this.enabled = Boolean(body?.enabled);
					this.items = body?.items ?? [];
					this.answeredWeek = body?.answered_week ?? 0;
					this.defaultHours = body?.default_hours ?? 24;
					this.fetchedAt = Date.now();
					this.now = Date.now();
					if (this.chatsOpen && !this.enabled) this.chatsOpen = false;
				});
			} catch (error) {
				logger.warn('Failed to load waiting items', error);
			} finally {
				runInAction(() => {
					this.loaded = true;
					this.inflight = null;
				});
			}
		})();
		return this.inflight;
	}

	setChatsOpen(open: boolean): void {
		this.chatsOpen = open;
		if (open) void this.refresh();
	}

	/** Done, Later, Not waiting any more: the item leaves the lists at once. */
	async act(id: string, action: WaitingAction, until?: Date): Promise<boolean> {
		const before = this.items;
		this.items = this.items.flatMap((item) => {
			if (item.id !== id) return [item];
			if (action === 'later') return [{...item, snoozed_until: (until ?? new Date(Date.now() + 864e5)).toISOString()}];
			return [];
		});
		this.drafts.delete(id);
		this.forgetBox(id);
		try {
			const response = await http.post<{item: WaitingItem | null}>(Endpoints.KONVO_ASSISTANT_WAITING_ACTION(id), {
				body: {action, ...(until ? {until: until.toISOString()} : {})},
			});
			const item = response.body?.item;
			runInAction(() => {
				if (item && item.status === 'open') this.items = [...this.items.filter((entry) => entry.id !== id), item];
			});
			return true;
		} catch (error) {
			logger.warn('Waiting action failed', error);
			runInAction(() => {
				this.items = before;
			});
			return false;
		}
	}

	/** Asks Konvo for a nudge (you waiting) or a reply (them waiting) on that channel. */
	async draft(id: string, via?: WaitingVia): Promise<void> {
		const item = this.byId(id);
		if (!item) return;
		const previous = this.drafts.get(id);
		this.drafts.set(id, {
			text: previous?.text ?? '',
			via: via ?? previous?.via ?? item.conversation.source,
			loading: true,
			sending: false,
			error: false,
		});
		try {
			const response = await http.post<WaitingDraftResponse>(Endpoints.KONVO_ASSISTANT_WAITING_DRAFT(id), {
				body: via ? {via} : {},
			});
			runInAction(() => {
				const body = response.body;
				if (!response.ok || !body?.text) throw new Error('draft_failed');
				this.drafts.set(id, {text: body.text, via: body.via, loading: false, sending: false, error: false});
			});
		} catch (error) {
			logger.warn('Waiting draft failed', error);
			runInAction(() => {
				const current = this.drafts.get(id);
				if (current) this.drafts.set(id, {...current, loading: false, error: true});
			});
		}
	}

	/**
	 * The bar's answer choices for someone waiting on you. Kept by the server until a newer message
	 * arrives, so this is cheap on every open; `fresh` asks for other answers, `via` another channel.
	 */
	async loadChoices(id: string, options: {fresh?: boolean; via?: WaitingVia} = {}): Promise<void> {
		const item = this.byId(id);
		const current = this.choices.get(id);
		if (!item || current?.loading) return;
		if (current && !current.error && !options.fresh && (!options.via || options.via === current.via)) return;
		this.choices.set(id, {
			choices: options.fresh ? [] : (current?.choices ?? []),
			via: options.via ?? current?.via ?? item.conversation.source,
			loading: true,
			error: false,
		});
		const body = await this.requestDraft(id, {
			...(options.fresh ? {fresh: true} : {}),
			...(options.via ? {via: options.via} : current?.via ? {via: current.via} : {}),
		});
		runInAction(() => {
			const choices = body?.choices?.length ? body.choices : body?.text ? [{label: '', text: body.text}] : [];
			this.choices.set(id, {
				choices,
				via: body?.via ?? options.via ?? item.conversation.source,
				loading: false,
				error: choices.length === 0,
			});
		});
	}

	/** One draft for the box: a nudge, or (with an instruction) a reply written from it. Null on failure. */
	async writeDraft(id: string, request: WaitingDraftRequest = {}): Promise<WaitingDraftResponse | null> {
		const body = await this.requestDraft(id, request);
		return body?.text ? body : null;
	}

	private async requestDraft(id: string, request: WaitingDraftRequest): Promise<WaitingDraftResponse | null> {
		try {
			const response = await http.post<WaitingDraftResponse>(Endpoints.KONVO_ASSISTANT_WAITING_DRAFT(id), {
				body: request,
			});
			return response.ok && response.body?.text ? response.body : null;
		} catch (error) {
			logger.warn('Waiting draft failed', error);
			return null;
		}
	}

	/** Konvo's draft went into the conversation's box (`before`: what the box held until then). */
	putInBox(key: string, itemId: string, via: WaitingVia, before: string): void {
		this.boxDrafts.set(key, {itemId, via, history: [before], changing: false, error: false});
	}

	/**
	 * Changes the box's text as asked ("Make it shorter."). Resolves to the new text, or null when
	 * Konvo could not (the box keeps its text).
	 */
	async changeBox(key: string, current: string, instruction: string): Promise<string | null> {
		const box = this.boxDrafts.get(key);
		if (!box || box.changing) return null;
		this.boxDrafts.set(key, {...box, changing: true, error: false});
		const body = await this.requestDraft(box.itemId, {via: box.via, instruction, current});
		const next = this.boxDrafts.get(key);
		if (!next) return null;
		runInAction(() => {
			this.boxDrafts.set(key, {
				...next,
				changing: false,
				error: !body,
				history: body ? [...next.history, current] : next.history,
			});
		});
		return body?.text ?? null;
	}

	/** Undo: the box's previous text. Past Konvo's first draft, the draft leaves the box (the bar returns). */
	undoBox(key: string): string | null {
		const box = this.boxDrafts.get(key);
		if (!box || box.changing) return null;
		const previous = box.history.at(-1) ?? '';
		if (box.history.length <= 1) this.boxDrafts.delete(key);
		else this.boxDrafts.set(key, {...box, history: box.history.slice(0, -1)});
		return previous;
	}

	clearBox(key: string): void {
		this.boxDrafts.delete(key);
	}

	private forgetBox(itemId: string): void {
		for (const [key, box] of this.boxDrafts) if (box.itemId === itemId) this.boxDrafts.delete(key);
		this.choices.delete(itemId);
	}

	/**
	 * The user sent from a conversation's box. With Konvo's answer in it, that answered the question:
	 * the item closes now (a nudge waits again), rather than when the ledger next reads the chat.
	 */
	sentInConversation(source: string, conversationId: string): void {
		const key = boxKey(source, conversationId);
		const box = this.boxDrafts.get(key);
		if (!box) return;
		this.boxDrafts.delete(key);
		const item = this.byId(box.itemId);
		if (item) void this.act(item.id, item.direction === 'on_you' ? 'replied' : 'nudged');
	}

	setDraftText(id: string, text: string): void {
		const current = this.drafts.get(id);
		if (current) this.drafts.set(id, {...current, text});
	}

	closeDraft(id: string): void {
		this.drafts.delete(id);
	}

	/** Sends the draft. A nudge keeps the item (Konvo waits again); a reply closes it. */
	async send(id: string): Promise<boolean> {
		const draft = this.drafts.get(id);
		if (!draft || !draft.text.trim()) return false;
		this.drafts.set(id, {...draft, sending: true, error: false});
		try {
			const response = await http.post<{item: WaitingItem | null}>(Endpoints.KONVO_ASSISTANT_WAITING_SEND(id), {
				body: {text: draft.text.trim(), via: draft.via},
			});
			if (!response.ok) throw new Error('send_failed');
			runInAction(() => {
				const item = response.body?.item ?? null;
				this.items = [...this.items.filter((entry) => entry.id !== id), ...(item ? [item] : [])];
				this.drafts.delete(id);
			});
			return true;
		} catch (error) {
			logger.warn('Waiting send failed', error);
			runInAction(() => {
				const current = this.drafts.get(id);
				if (current) this.drafts.set(id, {...current, sending: false, error: true});
			});
			return false;
		}
	}

	/** The corner's "Draft a nudge": open the item's conversation with its draft started. */
	focus(id: string | null): void {
		this.focusId = id;
	}

	directionOf(id: string): WaitingDirection | null {
		return this.byId(id)?.direction ?? null;
	}
}

export default new WaitingStore();
